import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Palette, Rocket, ChevronLeft, ChevronRight } from 'lucide-react';

const ThreeDHeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  
  // Mouse position tracking
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth spring animations for parallax
  const smoothMouseX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const smoothMouseY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  // Parallax transforms for different layers
  // Z-1: Background (slowest, inverted)
  const bgX = useTransform(smoothMouseX, [-500, 500], [15, -15]);
  const bgY = useTransform(smoothMouseY, [-300, 300], [10, -10]);
  
  // Z-2: Far elements (slow)
  const farX = useTransform(smoothMouseX, [-500, 500], [30, -30]);
  const farY = useTransform(smoothMouseY, [-300, 300], [20, -20]);
  
  // Z-3: Pop-out elements (fast, creates depth)
  const popX = useTransform(smoothMouseX, [-500, 500], [-60, 60]);
  const popY = useTransform(smoothMouseY, [-300, 300], [-40, 40]);
  
  // Z-4: Text (minimal movement for readability)
  const textX = useTransform(smoothMouseX, [-500, 500], [-5, 5]);
  const textY = useTransform(smoothMouseY, [-300, 300], [-3, 3]);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mouse move handler
  const handleMouseMove = (e) => {
    if (isMobile || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    mouseX.set(e.clientX - centerX);
    mouseY.set(e.clientY - centerY);
  };

  // Auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % 3);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + 3) % 3);

  // Slide configurations
  const slides = [
    {
      id: 0,
      theme: 'cyan',
      gradient: 'from-[#0a1628] via-[#0d2137] to-[#0a1628]',
      accentColor: '#00cec9',
      secondaryColor: '#0984e3',
      title: 'VITESSE DU CODE.',
      titleAccent: 'IMPACT RÉEL.',
      subtitle: 'Des sites React qui chargent avant même que vous ayez cliqué.',
      cta: 'Voir la performance',
      ctaLink: '/services',
      icon: <Zap size={24} />
    },
    {
      id: 1,
      theme: 'violet',
      gradient: 'from-[#1a0a28] via-[#2d1145] to-[#1a0a28]',
      accentColor: '#6c5ce7',
      secondaryColor: '#e84393',
      title: 'LE DESIGN QUI',
      titleAccent: 'SORT DU CADRE.',
      subtitle: 'Nous ne faisons pas des pages. Nous créons des expériences.',
      cta: 'Notre portfolio',
      ctaLink: '/agence',
      icon: <Palette size={24} />
    },
    {
      id: 2,
      theme: 'gold',
      gradient: 'from-[#1a1408] via-[#2d2210] to-[#1a1408]',
      accentColor: '#fdcb6e',
      secondaryColor: '#e17055',
      title: 'VOTRE BUSINESS',
      titleAccent: 'EN 3D.',
      subtitle: 'Passez dans la dimension supérieure. Estimez votre projet VR-Ready.',
      cta: 'Lancer le Simulateur',
      ctaLink: '/simulateur',
      icon: <Rocket size={24} />
    }
  ];

  const currentSlideData = slides[currentSlide];

  return (
    <div 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative w-full h-[90vh] min-h-[600px] overflow-hidden cursor-crosshair"
      style={{ perspective: '1000px' }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
        >
          {/* ============================================= */}
          {/* Z-1: BACKGROUND LAYER (Deepest, slowest) */}
          {/* ============================================= */}
          <motion.div 
            className={`absolute inset-[-50px] bg-gradient-to-br ${currentSlideData.gradient}`}
            style={!isMobile ? { x: bgX, y: bgY } : {}}
          >
            {/* Deep grid pattern */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px'
            }}></div>
            
            {/* Deep radial glow */}
            <div className="absolute inset-0" style={{
              background: `radial-gradient(ellipse 80% 50% at 50% 50%, ${currentSlideData.accentColor}15 0%, transparent 70%)`
            }}></div>
          </motion.div>

          {/* ============================================= */}
          {/* Z-2: FAR ELEMENTS (Slow movement) */}
          {/* ============================================= */}
          <motion.div 
            className="absolute inset-0 pointer-events-none"
            style={!isMobile ? { x: farX, y: farY } : {}}
          >
            {/* Floating geometric shapes - blurred, distant */}
            {currentSlideData.theme === 'cyan' && (
              <>
                {/* Horizontal speed lines */}
                <div className="absolute top-[20%] left-0 w-full h-[2px] opacity-20"
                  style={{ background: `linear-gradient(90deg, transparent, ${currentSlideData.accentColor}, transparent)` }}></div>
                <div className="absolute top-[40%] left-0 w-full h-[1px] opacity-10"
                  style={{ background: `linear-gradient(90deg, transparent, ${currentSlideData.secondaryColor}, transparent)` }}></div>
                <div className="absolute top-[60%] left-0 w-full h-[2px] opacity-15"
                  style={{ background: `linear-gradient(90deg, transparent, ${currentSlideData.accentColor}, transparent)` }}></div>
                <div className="absolute top-[80%] left-0 w-full h-[1px] opacity-20"
                  style={{ background: `linear-gradient(90deg, transparent, ${currentSlideData.secondaryColor}, transparent)` }}></div>
                
                {/* Blurred circles */}
                <div className="absolute top-[10%] left-[10%] w-32 h-32 rounded-full blur-[60px] opacity-30"
                  style={{ background: currentSlideData.accentColor }}></div>
                <div className="absolute bottom-[20%] right-[15%] w-48 h-48 rounded-full blur-[80px] opacity-20"
                  style={{ background: currentSlideData.secondaryColor }}></div>
              </>
            )}
            
            {currentSlideData.theme === 'violet' && (
              <>
                {/* Crystal grid pattern */}
                <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <polygon points="50,0 100,50 50,100 0,50" fill="none" stroke={currentSlideData.accentColor} strokeWidth="0.2" />
                  <polygon points="50,20 80,50 50,80 20,50" fill="none" stroke={currentSlideData.secondaryColor} strokeWidth="0.15" />
                </svg>
                
                {/* Floating orbs */}
                <div className="absolute top-[15%] right-[20%] w-40 h-40 rounded-full blur-[70px] opacity-25"
                  style={{ background: currentSlideData.accentColor }}></div>
                <div className="absolute bottom-[25%] left-[10%] w-56 h-56 rounded-full blur-[90px] opacity-20"
                  style={{ background: currentSlideData.secondaryColor }}></div>
              </>
            )}
            
            {currentSlideData.theme === 'gold' && (
              <>
                {/* Circuit pattern */}
                <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice">
                  <path d="M0,100 L50,100 L50,50 L100,50 L100,100 L150,100 L150,150 L200,150" 
                    fill="none" stroke={currentSlideData.accentColor} strokeWidth="0.5" />
                  <path d="M0,50 L30,50 L30,80 L80,80 L80,120 L130,120 L130,80 L200,80" 
                    fill="none" stroke={currentSlideData.secondaryColor} strokeWidth="0.3" />
                </svg>
                
                {/* Warm glows */}
                <div className="absolute top-[20%] left-[25%] w-48 h-48 rounded-full blur-[80px] opacity-25"
                  style={{ background: currentSlideData.accentColor }}></div>
                <div className="absolute bottom-[15%] right-[20%] w-64 h-64 rounded-full blur-[100px] opacity-20"
                  style={{ background: currentSlideData.secondaryColor }}></div>
              </>
            )}
          </motion.div>

          {/* ============================================= */}
          {/* Z-3: POP-OUT ELEMENTS (Fast, overflow visible) */}
          {/* ============================================= */}
          <motion.div 
            className="absolute inset-0 pointer-events-none overflow-visible"
            style={!isMobile ? { 
              x: popX, 
              y: popY
            } : {}}
            initial={isMobile ? { scale: 0.8, rotateX: 10 } : {}}
            animate={isMobile ? { scale: 1, rotateX: 0 } : {}}
            transition={{ duration: 1.5, ease: "easeOut" }}
          >
            {/* SLIDE 1: Light trails shooting towards viewer */}
            {currentSlideData.theme === 'cyan' && (
              <>
                {/* Light trails */}
                <motion.div 
                  className="absolute top-[30%] -left-[10%] w-[120%] h-1"
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 1 }}
                  transition={{ duration: 1.2, delay: 0.3 }}
                  style={{ 
                    background: `linear-gradient(90deg, transparent, ${currentSlideData.accentColor}, ${currentSlideData.accentColor}, transparent)`,
                    boxShadow: `0 0 20px ${currentSlideData.accentColor}, 0 0 40px ${currentSlideData.accentColor}`,
                    transformOrigin: 'left center'
                  }}
                ></motion.div>
                <motion.div 
                  className="absolute top-[50%] -left-[5%] w-[110%] h-[3px]"
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 0.8 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  style={{ 
                    background: `linear-gradient(90deg, transparent, ${currentSlideData.secondaryColor}, transparent)`,
                    boxShadow: `0 0 30px ${currentSlideData.secondaryColor}`,
                    transformOrigin: 'left center'
                  }}
                ></motion.div>
                <motion.div 
                  className="absolute top-[70%] -left-[8%] w-[116%] h-[2px]"
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: 0.6 }}
                  transition={{ duration: 1.4, delay: 0.2 }}
                  style={{ 
                    background: `linear-gradient(90deg, transparent, ${currentSlideData.accentColor}, transparent)`,
                    boxShadow: `0 0 15px ${currentSlideData.accentColor}`,
                    transformOrigin: 'left center'
                  }}
                ></motion.div>
                
                {/* Floating energy orb - pops out */}
                <motion.div 
                  className="absolute top-[25%] right-[5%] w-24 h-24 rounded-full"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  style={{ 
                    background: `radial-gradient(circle, ${currentSlideData.accentColor}80 0%, ${currentSlideData.accentColor}00 70%)`,
                    boxShadow: `0 0 60px ${currentSlideData.accentColor}, 0 0 100px ${currentSlideData.accentColor}50`
                  }}
                ></motion.div>
              </>
            )}
            
            {/* SLIDE 2: Floating crystals/geometric shapes */}
            {currentSlideData.theme === 'violet' && (
              <>
                {/* Crystal 1 - Large, front */}
                <motion.div 
                  className="absolute top-[15%] right-[10%] w-32 h-32"
                  initial={{ scale: 0, rotate: -20, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  transition={{ duration: 1, delay: 0.3 }}
                  style={{ 
                    background: `linear-gradient(135deg, ${currentSlideData.accentColor}40, ${currentSlideData.secondaryColor}30)`,
                    clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
                    boxShadow: `0 0 40px ${currentSlideData.accentColor}60`,
                    backdropFilter: 'blur(4px)'
                  }}
                ></motion.div>
                
                {/* Crystal 2 - Medium */}
                <motion.div 
                  className="absolute bottom-[20%] left-[15%] w-20 h-20"
                  initial={{ scale: 0, rotate: 30, opacity: 0 }}
                  animate={{ scale: 1, rotate: 15, opacity: 0.8 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  style={{ 
                    background: `linear-gradient(225deg, ${currentSlideData.secondaryColor}50, ${currentSlideData.accentColor}30)`,
                    clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                    boxShadow: `0 0 30px ${currentSlideData.secondaryColor}50`
                  }}
                ></motion.div>
                
                {/* Crystal 3 - Small, floating */}
                <motion.div 
                  className="absolute top-[60%] right-[25%] w-12 h-12"
                  initial={{ scale: 0, rotate: -45, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 0.9 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                  style={{ 
                    background: `linear-gradient(180deg, ${currentSlideData.accentColor}60, transparent)`,
                    clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
                    boxShadow: `0 0 20px ${currentSlideData.accentColor}`
                  }}
                ></motion.div>
                
                {/* Floating ring */}
                <motion.div 
                  className="absolute top-[40%] left-[5%] w-40 h-40 rounded-full border-2 opacity-40"
                  initial={{ scale: 0, rotate: 0 }}
                  animate={{ scale: 1, rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  style={{ borderColor: currentSlideData.accentColor }}
                ></motion.div>
              </>
            )}
            
            {/* SLIDE 3: Circuit connections */}
            {currentSlideData.theme === 'gold' && (
              <>
                {/* Circuit node 1 */}
                <motion.div 
                  className="absolute top-[20%] right-[15%]"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className="w-6 h-6 rounded-full" style={{ 
                    background: currentSlideData.accentColor,
                    boxShadow: `0 0 30px ${currentSlideData.accentColor}, 0 0 60px ${currentSlideData.accentColor}50`
                  }}></div>
                </motion.div>
                
                {/* Circuit lines connecting outside frame */}
                <motion.svg 
                  className="absolute inset-0 w-full h-full overflow-visible"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 2, delay: 0.4 }}
                >
                  <motion.path 
                    d="M 85% 22% L 105% 22% L 105% 50% L 115% 50%"
                    fill="none"
                    stroke={currentSlideData.accentColor}
                    strokeWidth="2"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                    style={{ filter: `drop-shadow(0 0 10px ${currentSlideData.accentColor})` }}
                  />
                  <motion.path 
                    d="M 15% 70% L -5% 70% L -5% 40%"
                    fill="none"
                    stroke={currentSlideData.secondaryColor}
                    strokeWidth="2"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.2, delay: 0.7 }}
                    style={{ filter: `drop-shadow(0 0 8px ${currentSlideData.secondaryColor})` }}
                  />
                </motion.svg>
                
                {/* Glowing data nodes */}
                <motion.div 
                  className="absolute bottom-[30%] left-[10%] w-4 h-4 rounded-full"
                  initial={{ scale: 0 }}
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{ 
                    background: currentSlideData.secondaryColor,
                    boxShadow: `0 0 20px ${currentSlideData.secondaryColor}`
                  }}
                ></motion.div>
                
                {/* Energy pulse ring */}
                <motion.div 
                  className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border opacity-20"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1.5, opacity: [0, 0.3, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  style={{ borderColor: currentSlideData.accentColor }}
                ></motion.div>
              </>
            )}
          </motion.div>

          {/* ============================================= */}
          {/* Z-4: TEXT & CTA (Minimal movement) */}
          {/* ============================================= */}
          <motion.div 
            className="absolute inset-0 flex items-center justify-center"
            style={!isMobile ? { x: textX, y: textY } : {}}
          >
            <div className="text-center max-w-5xl px-6">
              {/* Title */}
              <motion.h1 
                className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-none tracking-tight"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="text-white block" style={{ textShadow: '0 0 40px rgba(0,0,0,0.5)' }}>
                  {currentSlideData.title}
                </span>
                <span 
                  className="block"
                  style={{ 
                    color: currentSlideData.accentColor,
                    textShadow: `0 0 60px ${currentSlideData.accentColor}80, 0 0 120px ${currentSlideData.accentColor}40`
                  }}
                >
                  {currentSlideData.titleAccent}
                </span>
              </motion.h1>
              
              {/* Subtitle */}
              <motion.p 
                className="text-xl md:text-2xl lg:text-3xl mb-10 max-w-3xl mx-auto"
                style={{ color: 'rgba(255, 255, 255, 0.85)' }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {currentSlideData.subtitle}
              </motion.p>
              
              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Link
                  to={currentSlideData.ctaLink}
                  className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold transition-all duration-300 hover:scale-105 group relative overflow-hidden"
                  style={{ 
                    background: currentSlideData.accentColor,
                    color: '#000',
                    boxShadow: `0 0 40px ${currentSlideData.accentColor}60, 0 0 80px ${currentSlideData.accentColor}30`
                  }}
                  data-testid={`hero-slider-cta-${currentSlide}`}
                >
                  <span className="absolute inset-0 bg-white/20 -translate-x-full group-hover:translate-x-full transition-transform duration-500"></span>
                  {currentSlideData.icon}
                  <span className="ml-3 mr-2">{currentSlideData.cta}</span>
                  <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* ============================================= */}
      {/* NAVIGATION CONTROLS */}
      {/* ============================================= */}
      {/* Arrows */}
      <button 
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-3 rounded-full transition-all duration-300 hover:scale-110 z-50"
        style={{ 
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}
        data-testid="hero-slider-prev"
      >
        <ChevronLeft size={28} className="text-white" />
      </button>
      
      <button 
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-3 rounded-full transition-all duration-300 hover:scale-110 z-50"
        style={{ 
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}
        data-testid="hero-slider-next"
      >
        <ChevronRight size={28} className="text-white" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 z-50">
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className="w-3 h-3 rounded-full transition-all duration-300"
            style={{ 
              background: currentSlide === index ? slides[index].accentColor : 'rgba(255,255,255,0.3)',
              boxShadow: currentSlide === index ? `0 0 15px ${slides[index].accentColor}` : 'none',
              transform: currentSlide === index ? 'scale(1.3)' : 'scale(1)'
            }}
            data-testid={`hero-slider-dot-${index}`}
          />
        ))}
      </div>

      {/* Slide Counter */}
      <div className="absolute bottom-8 right-8 text-white/50 text-sm font-mono z-50">
        <span style={{ color: currentSlideData.accentColor }}>{String(currentSlide + 1).padStart(2, '0')}</span>
        <span> / 03</span>
      </div>
    </div>
  );
};

export default ThreeDHeroSlider;
